import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "../utils";
import { Button } from "../button";
import { Spinner } from "../spinner";

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  portal?: Element | false;
};

export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  className,
  portal = document.body,
}: DialogProps) {
  const [visible, setVisible] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(true);
      // Delay animation start to allow render
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimate(true));
      });
    } else {
      setAnimate(false);
      const timer = setTimeout(() => setVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!visible) return null;

  const content = (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/30 transition-opacity duration-200",
          animate ? "opacity-100" : "opacity-0"
        )}
        aria-hidden="true"
      />

      {/* Dialog container - clicks outside panel close the dialog */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          role="dialog"
          aria-modal="true"
          className={cn(
            "w-full max-w-md rounded-xl bg-white p-6 shadow-xl",
            "dark:bg-gray-800",
            "transition-all duration-200",
            animate ? "opacity-100 scale-100" : "opacity-0 scale-95",
            className
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {subtitle}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close dialog"
            >
              <X className="size-5" />
            </button>
          </div>

          <div className="mt-2">{children}</div>

          {footer && <div className="mt-6 flex justify-end gap-3">{footer}</div>}
        </div>
      </div>
    </div>
  );

  return portal ? createPortal(content, portal) : content;
}

export type ConfirmDialogVariant = "default" | "destructive";

export type ConfirmDialogProps = Omit<DialogProps, "children" | "footer"> & {
  description: string;
  confirm: {
    label: string;
    onClick: () => void;
  };
  cancel?: {
    label: string;
    onClick?: () => void;
  };
  variant?: ConfirmDialogVariant;
  loading?: boolean;
};

export function ConfirmDialog({
  description,
  confirm,
  cancel,
  variant = "default",
  loading = false,
  onClose,
  ...dialogProps
}: ConfirmDialogProps) {
  const handleCancel = () => {
    cancel?.onClick?.();
    onClose();
  };

  const handleConfirm = () => {
    confirm.onClick();
  };

  return (
    <Dialog {...dialogProps} onClose={onClose}>
      <p className="text-sm text-gray-600 dark:text-gray-300">{description}</p>

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="white" onClick={handleCancel} disabled={loading}>
          {cancel?.label ?? "Cancel"}
        </Button>
        <Button
          variant={variant === "destructive" ? "destructive" : "primary"}
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading ? <Spinner size="xs" variant="white" /> : null}
          {confirm.label}
        </Button>
      </div>
    </Dialog>
  );
}
